import { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import {
  getCompany,
  deleteCompany,
  updateCompany,
  changeIntegrationStatusOfCompany,
  getIntegrationStatus,
} from "../../Actions/CompanyAction";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CachedIcon from "@mui/icons-material/Cached";
import { IconButton, Tooltip, Box, Chip } from "@mui/material";
import Swal from "sweetalert2";
import { isUndefined, debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import ChangeStatusModal from "./ChangeStatusModal";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ChangeIntegrationStatusModal from "./ChangeIntegrationStatusModal";
import VisibilityIcon from "@mui/icons-material/Visibility";

const CompaniesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [
    changeIntegrationStatusModalOpen,
    setChangeIntegrationStatusModalOpen,
  ] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const isGettingCompanyList = useSelector(
    (state) => state.CompanyListReducer?.isGettingCompanyList,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const totalResults = useSelector(
    (state) => state.CompanyListReducer?.totalResults || 0,
  );
  const { userDetails } = useSelector((state) => state?.HeaderReducer);

  useEffect(() => {
    dispatch(
      getCompany(pagination.pageIndex, pagination.pageSize, globalFilter),
    );
  }, [pagination, dispatch, globalFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getIntegrationStatus());
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value) => {
        setGlobalFilter(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  const handleDeleteCompany = (row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "mui-swal-popup",
      },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) {
          container.style.zIndex = "2000"; // higher than MUI
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteCompany(
            row.original._id,
            handleVisible,
            handleDangerVisible,
            pagination.pageIndex,
            pagination.pageSize,
            globalFilter,
          ),
        );
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Edit: () => null,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "companyName",
        header: "Company Name",
        size: 150, // slightly wider
        grow: 1, // fixed width
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          placeholder: "Enter Company Name",
        },
      },
      {
        accessorKey: "companyStatus",
        header: "Company Status",
        size: 120,
        grow: 1,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 1,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          let color = "default";
          let bgColor = "#e0e0e0";
          if (value === "Sandbox") {
            bgColor = "#ff9800";
            color = "#fff";
          } else if (value === "Production") {
            bgColor = "#4caf50";
            color = "#fff";
          }
          return (
            <Chip
              label={cell.getValue() || "-"}
              size="small"
              sx={{
                backgroundColor: bgColor,
                color: color,
                fontWeight: 500,
              }}
            />
          );
        },
      },
      {
        accessorKey: "ntn",
        header: "NTN Number",
        size: 120,
        grow: 1, // take flexible space
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 1,
          placeholder: "Enter NTN Number",
        },
      },
      {
        accessorKey: "posId",
        header: "POS ID",
        size: 100,
        grow: 1,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 1,
          placeholder: "Enter POS ID",
        },
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "CEOContact",
        header: "Contact Number",
        size: 120,
        grow: 1,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 1,
          placeholder: "Enter Contact Number",
        },
      },
      {
        accessorKey: "CEOEmail",
        header: "Email Address",
        size: 120,
        grow: 1,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 1,
          placeholder: "Enter Email Address",
        },
      },
      {
        accessorKey: "province",
        header: "Province",
        size: 123,
        grow: 1,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 1,
          placeholder: "Enter Province",
        },
      },
      {
        accessorKey: "createdBy.email",
        header: "Created By",
        grow: true,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Created By Email",
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 100,
        grow: 1, // fixed width
        enableEditing: false,
        Edit: () => null,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" flexWrap="wrap">
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(
                    `/main/view-company-details?id=${row?.original?._id}`,
                  )
                }
                sx={{
                  color: "black",
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() =>
                  navigate("/main/edit-company", { state: row.original })
                }
                sx={{ color: "black" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Change Status">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedCompany(row.original);
                  setChangeStatusModalOpen(true);
                }}
                sx={{ color: "black" }}
              >
                <CachedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteCompany(row)}
                sx={{ color: "black" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {userDetails?.role === "admin" && (
              <Tooltip title="Change Integration Status">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedCompany(row.original);
                    setChangeIntegrationStatusModalOpen(true);
                  }}
                  sx={{ color: "black" }}
                >
                  <IntegrationInstructionsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line
    [pagination],
  );

  const renderMobileCompanyCards = () => {
    return companiesList.map((item, index) => {
      const serial = index + 1 + pagination.pageIndex * pagination.pageSize;

      let bgColor = "#e0e0e0";
      let color = "#000";

      if (item.companyStatus === "Sandbox") {
        bgColor = "#ff9800";
        color = "#fff";
      } else if (item.companyStatus === "Production") {
        bgColor = "#4caf50";
        color = "#fff";
      }

      return (
        <Box
          key={item._id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            {/* Serial */}
            <Box sx={{ fontSize: 12, color: "#999" }}>#{serial}</Box>

            {/* Actions */}
            <Box display="flex" gap={1}>
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/main/view-company-details?id=${item?._id}`)
                  }
                  sx={{
                    color: "black",
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate("/main/edit-company", { state: item })
                  }
                  sx={{ color: "black" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Change Status">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedCompany(item);
                    setChangeStatusModalOpen(true);
                  }}
                  sx={{ color: "black" }}
                >
                  <CachedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteCompany({ original: item })}
                  sx={{ color: "black" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {userDetails?.role === "admin" && (
                <Tooltip title="Change Integration Status">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedCompany(item);
                      setChangeIntegrationStatusModalOpen(true);
                    }}
                    sx={{ color: "black" }}
                  >
                    <IntegrationInstructionsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Company Name */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Company Name:</b>
            <Box sx={{ fontSize: 14 }}>{item.companyName || "-"}</Box>
          </Box>

          {/* Status */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Status:</b>
            <Box mt={0.5}>
              <Chip
                label={item.companyStatus || "-"}
                size="small"
                sx={{
                  backgroundColor: bgColor,
                  color: color,
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>

          {/* NTN */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>NTN:</b>
            <Box sx={{ fontSize: 14 }}>{item.ntn || "-"}</Box>
          </Box>

          {/* POS ID */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>POS ID:</b>
            <Box sx={{ fontSize: 14 }}>{item.posId || "-"}</Box>
          </Box>

          {/* Contact */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Contact:</b>
            <Box sx={{ fontSize: 14 }}>{item.CEOContact || "-"}</Box>
          </Box>

          {/* Email */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Email:</b>
            <Box sx={{ fontSize: 14 }}>{item.CEOEmail || "-"}</Box>
          </Box>

          {/* Province */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Province:</b>
            <Box sx={{ fontSize: 14 }}>{item.province || "-"}</Box>
          </Box>

          {/* Created By */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Created By:</b>
            <Box sx={{ fontSize: 14 }}>{item?.createdBy?.email || "-"}</Box>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap", // important for mobile
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall, // mobile
                sm: globalStyle.headingFontSize, // tablet+
              },
              lineHeight: 1.2,
            }}
          >
            Companies
          </Box>

          {/* Optional subtitle (mobile friendly) */}
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: {
                xs: 12,
                sm: 14,
              },
              color: "#666",
            }}
          >
            Manage and view all Companies records
          </Box>
        </Box>
      </Box>
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingCompanyList}
          searchLabel="Companies"
        >
          {renderMobileCompanyCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={companiesList}
          enableRowActions={false}
          manualPagination
          rowCount={totalResults}
          state={{
            columnOrder: [
              "_id",
              "companyName",
              "companyStatus",
              "ntn",
              "posId",
              "CEOContact",
              "CEOEmail",
              "province",
              "createdBy.email",
              "actions",
            ],
            columnPinning: {
              right: ["actions"],
            },
            isLoading: isGettingCompanyList,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor: globalStyle.bgColor,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none", // ✅ remove height restriction
              overflow: "visible", // ✅ allow content to expand
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
        />
      )}
      <ChangeStatusModal
        open={changeStatusModalOpen}
        onClose={() => {
          setChangeStatusModalOpen(false);
          setSelectedCompany(null);
        }}
        rowData={selectedCompany}
        onSubmit={(id, value, handleClose) => {
          dispatch(
            updateCompany(
              id,
              { companyStatus: value },
              handleVisible,
              handleDangerVisible,
              () => {
                setChangeStatusModalOpen(false);
                setSelectedCompany(null);
                handleClose();
              },
              true,
              pagination.pageIndex,
              pagination.pageSize,
              globalFilter,
            ),
          );
        }}
      />
      <ChangeIntegrationStatusModal
        open={changeIntegrationStatusModalOpen}
        onClose={() => {
          setChangeIntegrationStatusModalOpen(false);
          setSelectedCompany(null);
        }}
        rowData={selectedCompany}
        onSubmit={(id, value, handleClose) => {
          dispatch(
            changeIntegrationStatusOfCompany(
              id,
              { integrationStatus: value },
              handleVisible,
              handleDangerVisible,
              () => {
                setChangeIntegrationStatusModalOpen(false);
                setSelectedCompany(null);
                handleClose();
              },
              pagination.pageIndex,
              pagination.pageSize,
              globalFilter,
            ),
          );
        }}
      />
    </Box>
  );
};

export default CompaniesList;
